import { Component, createRef } from 'react';
import { Animated, Text, View } from 'react-native';

import IconShopMore from '../../../assets/svg/IconShopMore';
import { checkItemViewed } from '../../../context/devLogFirehose';
import ScrollController from '../../../elements/ScrollController';
import Touchable from '../../../elements/Touchable';
import { translate } from '../../../i18n';
import { TopSalesPayload } from '../../../redux/actions/getTopSalesActions';
import { Product as TopSalesProduct } from '../../../types/TopSales';
import { navigate } from '../../../utils/navigationService';
import ProductStorage from '../../../utils/ProductStorage';
import FlatListWithImpression from '../../FlatListWithImpression';
import { itemProducer } from '../../FlatListWithImpression/utils';
import ListItem from '../../ListItem';
import S, { ITEM_MARGIN, ITEM_WIDTH } from './styles';

export interface TopSalesRefMethods {
  setIsVisible: (isVisible: boolean) => void;
}

export interface TopSalesListProps {
  getTopSalesList: (payload: TopSalesPayload) => void;
  topSalesIdList: string[];
  isHomePageReload: boolean;
  isFocused: boolean;
}

export interface TopSalesListState {
  isVisible: boolean;
}

export default class TopSalesList extends Component<
  TopSalesListProps,
  TopSalesListState
> {
  static defaultProps: {
    testID: 'TopSalesList';
  };

  constructor(props: TopSalesListProps) {
    super(props);

    this.state = {
      isVisible: false,
    };
  }

  impressionList = createRef<typeof FlatListWithImpression>();
  layoutName = 'index_top';
  topSalesSources = new ProductStorage<TopSalesProduct>({
    productKey: product => `${product.id}`,
    productData: product => product.source,
  });

  keyExtractor = item => `${item}`;

  public setIsVisible = (isVisible: boolean) => {
    if (this.state.isVisible !== isVisible) {
      this.setState({ isVisible });
    }
  };

  onRefresh = () => {
    this.impressionList.current?.clearImpressionAccumulatedItems();

    this.getList({ loadingType: 'reload' });
  };

  componentDidMount() {
    this.getList();
  }

  componentDidUpdate(prevProps: TopSalesListProps) {
    const { isHomePageReload } = this.props;

    if (!prevProps.isHomePageReload && isHomePageReload) {
      this.onRefresh();
    }
  }

  getList = (args: Partial<TopSalesPayload> = {}) => {
    const { getTopSalesList } = this.props;

    getTopSalesList({
      success: this.topSalesSources.processData,
      loadingType: 'new',
      page: 1,
      limit: 20,
      ...args,
    });
  };

  onPressMoreTopSales = () => {
    navigate('TopSalesListPage');
  };

  renderItem = ({ item, index }: { item: string; index: number }) => {
    return (
      <ListItem
        index={index}
        key={item}
        id={item}
        numColumns={1}
        // PM: 橫向列表不顯示 ribbon、劃掉的價格
        showRibbon={false}
        showLineThroughPrice={false}
        showLoading={`${item}`.includes('blank')}
        itemWidth={ITEM_WIDTH}
        itemMargin={ITEM_MARGIN}
        contentWrapperStyle={S.contentWrapperStyle}
        titleStyle={S.ListItemFontStyle}
        priceCurrencyStyle={S.ListItemFontStyle}
        priceTildeStyle={S.ListItemFontStyle}
        priceStyle={S.ListItemFontStyle}
        hasHotSaleIcon
        wrapperStyle={S.listItemWrapperStyle}
        layoutName={this.layoutName}
        showTitlesAndPrice
        source={this.topSalesSources.get(item)}
        shouldDisplayImpressionForDebug={checkItemViewed(
          this.impressionList?.current?.accumulatedItems,
          item
        )}
        isShowItemLabel={false}
        linesOfTitle={1}
        showSKUTag
        showVendor
        isHomePageClick
      />
    );
  };

  ListFooter = () => {
    return (
      <Touchable
        accessibilityRole="button"
        onPress={this.onPressMoreTopSales}
        style={S.watchMoreContainer}
      >
        <View style={S.watchMoreTextSection}>
          <IconShopMore />
          <Text style={S.watchMoreText}>{translate('watch-more')}</Text>
        </View>
      </Touchable>
    );
  };

  scrollOffset = new Animated.Value(0);

  render() {
    const { topSalesIdList } = this.props;

    if (!topSalesIdList?.length) {
      return null;
    }

    return (
      <View style={S.horizontalContainer}>
        <FlatListWithImpression
          ref={this.impressionList}
          contentContainerStyle={S.horizontalListContainer}
          data={topSalesIdList}
          renderItem={this.renderItem}
          keyExtractor={this.keyExtractor}
          horizontal
          showsHorizontalScrollIndicator={false}
          layoutName={this.layoutName}
          isVisible={this.state.isVisible}
          sources={this.topSalesSources}
          dataProducer={itemProducer}
          ListFooterComponent={this.ListFooter}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { x: this.scrollOffset } } }],
            {
              useNativeDriver: false,
            }
          )}
        />
        <ScrollController
          scrollRef={this.impressionList}
          scrollOffset={this.scrollOffset}
          dataLength={topSalesIdList?.length || 0}
        />
      </View>
    );
  }
}
